import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const DigitalCanvas = ({ onComplete, mode = 'doodle' }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState('brush');
  const [currentColor, setCurrentColor] = useState('#3B82F6');
  const [brushSize, setBrushSize] = useState([8]);
  const [canvasHistory, setCanvasHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [drawingTime, setDrawingTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(null);

  // Drawing prompts based on mode
  const drawingPrompts = {
    doodle: [
      "Draw how you're feeling right now using shapes and colors",
      "Create a pattern that makes you feel calm",
      "Doodle your perfect day as abstract shapes",
      "Draw the weather inside your heart today",
      "Create a maze that leads to something good",
      "Doodle your energy level using lines and curves",
      "Draw a garden of your thoughts",
      "Create a mandala with your favorite symbols"
    ],
    emotion: [
      "Draw your anxiety as a creature, then give it friendly features",
      "Illustrate what happiness looks like in your world",
      "Draw anger as a storm, then add a rainbow",
      "Create a safe space using colors and shapes",
      "Draw what confidence would wear as an outfit",
      "Illustrate your worries floating away like balloons",
      "Draw yourself as a superhero with mental health powers",
      "Create a before and after of a difficult emotion"
    ],
    future: [
      "Draw yourself achieving a goal you're working toward",
      "Illustrate your dream room or space",
      "Draw what your ideal day looks like",
      "Create a map to your future self",
      "Draw the job or career that excites you",
      "Illustrate yourself helping others",
      "Draw a place where you feel completely yourself",
      "Create your personal logo or symbol for strength"
    ]
  };

  // Color palettes based on different moods/themes
  const colorPalettes = {
    calm: ['#E0F2FE', '#BAE6FD', '#7DD3FC', '#38BDF8', '#0EA5E9', '#0284C7', '#0369A1'],
    energetic: ['#FEF3C7', '#FDE68A', '#FCD34D', '#FBBF24', '#F59E0B', '#D97706', '#B45309'],
    nature: ['#ECFCCB', '#D9F99D', '#BEF264', '#A3E635', '#84CC16', '#65A30D', '#4D7C0F'],
    warm: ['#FEE2E2', '#FECACA', '#FCA5A5', '#F87171', '#EF4444', '#DC2626', '#B91C1C'],
    cool: ['#F0F9FF', '#E0F2FE', '#BAE6FD', '#7DD3FC', '#38BDF8', '#0EA5E9', '#0284C7'],
    sunset: ['#FFF7ED', '#FFEDD5', '#FED7AA', '#FDBA74', '#FB923C', '#F97316', '#EA580C'],
    rainbow: ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899']
  };

  const tools = [
    { id: 'brush', name: 'Brush', icon: '🖌️', cursor: 'crosshair' },
    { id: 'pencil', name: 'Pencil', icon: '✏️', cursor: 'crosshair' },
    { id: 'marker', name: 'Marker', icon: '🖊️', cursor: 'crosshair' },
    { id: 'eraser', name: 'Eraser', icon: '🧹', cursor: 'grab' },
    { id: 'spray', name: 'Spray', icon: '💨', cursor: 'crosshair' }
  ];

  // Canvas templates
  const templates = [
    { id: 'blank', name: 'Blank Canvas', preview: '⬜' },
    { id: 'circle', name: 'Circle', preview: '⭕' },
    { id: 'heart', name: 'Heart', preview: '💚' },
    { id: 'mandala', name: 'Mandala Base', preview: '🌸' },
    { id: 'grid', name: 'Grid', preview: '📊' },
    { id: 'nature', name: 'Tree Outline', preview: '🌳' }
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Select random prompt based on mode
      const prompts = drawingPrompts[mode] || drawingPrompts.doodle;
      const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
      setCurrentPrompt(randomPrompt);
    }
  }, [mode]);

  useEffect(() => {
    let timer;
    if (isActive) {
      timer = setInterval(() => {
        setDrawingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isActive]);

  const saveState = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const newHistory = canvasHistory.slice(0, historyStep + 1);
      newHistory.push(canvas.toDataURL());
      setCanvasHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    }
  };

  const startDrawing = (e) => {
    setIsActive(true);
    setIsDrawing(true);
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    saveState();
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const ctx = canvas.getContext('2d');
    
    ctx.lineWidth = brushSize[0];
    ctx.lineCap = 'round';
    
    switch (currentTool) {
      case 'brush':
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = currentColor;
        ctx.globalAlpha = 0.8;
        break;
      case 'pencil':
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = currentColor;
        ctx.globalAlpha = 1;
        ctx.lineWidth = Math.max(brushSize[0] * 0.7, 1);
        break;
      case 'marker':
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = currentColor;
        ctx.globalAlpha = 0.6;
        ctx.lineWidth = brushSize[0] * 1.5;
        break;
      case 'eraser':
        ctx.globalCompositeOperation = 'destination-out';
        ctx.globalAlpha = 1;
        break;
      case 'spray':
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 0.1;
        for (let i = 0; i < 20; i++) {
          const offsetX = (Math.random() - 0.5) * brushSize[0];
          const offsetY = (Math.random() - 0.5) * brushSize[0];
          ctx.fillStyle = currentColor;
          ctx.fillRect(x + offsetX, y + offsetY, 1, 1);
        }
        return;
    }
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveState();
  };

  const undo = () => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = canvasHistory[historyStep - 1];
    }
  };

  const redo = () => {
    if (historyStep < canvasHistory.length - 1) {
      setHistoryStep(historyStep + 1);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = canvasHistory[historyStep + 1];
    }
  };

  const downloadDrawing = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `my-artwork-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const completeDrawing = () => {
    const canvas = canvasRef.current;
    if (onComplete) {
      onComplete({
        type: 'digital_art',
        mode: mode,
        duration: drawingTime,
        artwork: canvas.toDataURL(),
        prompt: currentPrompt
      });
    }
  };

  const applyTemplate = (templateId) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 2;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    switch (templateId) {
      case 'circle':
        ctx.beginPath();
        ctx.arc(centerX, centerY, 100, 0, 2 * Math.PI);
        ctx.stroke();
        break;
      case 'heart':
        ctx.beginPath();
        ctx.moveTo(centerX, centerY + 20);
        ctx.bezierCurveTo(centerX, centerY - 20, centerX - 50, centerY - 40, centerX - 50, centerY - 10);
        ctx.bezierCurveTo(centerX - 50, centerY + 10, centerX, centerY + 30, centerX, centerY + 60);
        ctx.bezierCurveTo(centerX, centerY + 30, centerX + 50, centerY + 10, centerX + 50, centerY - 10);
        ctx.bezierCurveTo(centerX + 50, centerY - 40, centerX, centerY - 20, centerX, centerY + 20);
        ctx.stroke();
        break;
      case 'mandala':
        for (let i = 0; i < 8; i++) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, 50 + i * 15, 0, 2 * Math.PI);
          ctx.stroke();
        }
        break;
      case 'grid':
        for (let i = 0; i < canvas.width; i += 40) {
          ctx.beginPath();
          ctx.moveTo(i, 0);
          ctx.lineTo(i, canvas.height);
          ctx.stroke();
        }
        for (let i = 0; i < canvas.height; i += 40) {
          ctx.beginPath();
          ctx.moveTo(0, i);
          ctx.lineTo(canvas.width, i);
          ctx.stroke();
        }
        break;
      case 'nature':
        // Simple tree outline
        ctx.beginPath();
        ctx.moveTo(centerX, canvas.height - 50);
        ctx.lineTo(centerX, centerY + 50);
        ctx.moveTo(centerX - 60, centerY);
        ctx.bezierCurveTo(centerX - 80, centerY - 40, centerX - 40, centerY - 60, centerX, centerY - 40);
        ctx.bezierCurveTo(centerX + 40, centerY - 60, centerX + 80, centerY - 40, centerX + 60, centerY);
        ctx.stroke();
        break;
    }
    
    setSelectedTemplate(templateId);
    saveState();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="digital-canvas">
      {/* Header with Prompt */}
      <Card className="glass-card p-6 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="text-center space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">
            🎨 Digital Art Studio
          </h2>
          {currentPrompt && (
            <div className="max-w-2xl mx-auto">
              <Badge className="mb-3 bg-purple-100 text-purple-800">Creative Prompt</Badge>
              <p className="text-lg italic text-gray-700">
                "{currentPrompt}"
              </p>
            </div>
          )}
          {isActive && (
            <div className="flex justify-center space-x-4 text-sm text-gray-600">
              <span>⏱️ {formatTime(drawingTime)}</span>
              <span>🎨 {currentTool.charAt(0).toUpperCase() + currentTool.slice(1)}</span>
              <span>🎯 {brushSize[0]}px</span>
            </div>
          )}
        </div>
      </Card>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Tools Panel */}
        <div className="space-y-4">
          {/* Drawing Tools */}
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">🛠️ Tools</h3>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
              {tools.map((tool) => (
                <Button
                  key={tool.id}
                  onClick={() => setCurrentTool(tool.id)}
                  variant={currentTool === tool.id ? "default" : "outline"}
                  className={`w-full justify-start ${
                    currentTool === tool.id ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  <span className="mr-2">{tool.icon}</span>
                  {tool.name}
                </Button>
              ))}
            </div>
          </Card>

          {/* Brush Size */}
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">📏 Brush Size</h3>
            <div className="space-y-3">
              <Slider
                value={brushSize}
                onValueChange={setBrushSize}
                max={50}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Fine (1px)</span>
                <span className="font-medium">{brushSize[0]}px</span>
                <span>Thick (50px)</span>
              </div>
              {/* Brush Preview */}
              <div className="flex justify-center">
                <div 
                  className="rounded-full"
                  style={{
                    width: `${Math.max(brushSize[0], 4)}px`,
                    height: `${Math.max(brushSize[0], 4)}px`,
                    backgroundColor: currentTool === 'eraser' ? '#f3f4f6' : currentColor,
                    border: currentTool === 'eraser' ? '2px dashed #9ca3af' : 'none'
                  }}
                />
              </div>
            </div>
          </Card>

          {/* Templates */}
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">📋 Templates</h3>
            <div className="grid grid-cols-3 gap-2">
              {templates.map((template) => (
                <Button
                  key={template.id}
                  onClick={() => applyTemplate(template.id)}
                  variant="outline"
                  className="aspect-square text-2xl p-2 btn-secondary"
                  title={template.name}
                >
                  {template.preview}
                </Button>
              ))}
            </div>
          </Card>
        </div>

        {/* Main Canvas Area */}
        <div className="lg:col-span-2 space-y-4">
          {/* Canvas Controls */}
          <div className="flex flex-wrap gap-2 justify-between">
            <div className="flex gap-2">
              <Button onClick={undo} variant="outline" className="btn-secondary" disabled={historyStep <= 0}>
                ↶ Undo
              </Button>
              <Button onClick={redo} variant="outline" className="btn-secondary" disabled={historyStep >= canvasHistory.length - 1}>
                ↷ Redo
              </Button>
              <Button onClick={clearCanvas} variant="outline" className="btn-secondary text-red-600">
                🗑️ Clear
              </Button>
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadDrawing} variant="outline" className="btn-secondary">
                💾 Save
              </Button>
              <Button onClick={completeDrawing} className="btn-primary">
                ✅ Complete
              </Button>
            </div>
          </div>

          {/* Canvas */}
          <Card className="glass-card p-4">
            <div className="relative">
              <canvas
                ref={canvasRef}
                width={600}
                height={400}
                className="w-full border-2 border-gray-200 rounded-lg cursor-crosshair bg-white shadow-inner"
                style={{ cursor: tools.find(t => t.id === currentTool)?.cursor || 'crosshair' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
              {!isActive && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 rounded-lg">
                  <div className="text-center text-gray-600">
                    <div className="text-4xl mb-2">🎨</div>
                    <p>Start drawing to begin your creative session!</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Color Panel */}
        <div className="space-y-4">
          {/* Current Color */}
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">🎨 Current Color</h3>
            <div className="space-y-3">
              <div 
                className="w-full h-16 rounded-lg border-4 border-white shadow-lg cursor-pointer"
                style={{ backgroundColor: currentColor }}
                onClick={() => setShowColorPicker(!showColorPicker)}
              />
              {showColorPicker && (
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => setCurrentColor(e.target.value)}
                  className="w-full h-10 rounded cursor-pointer"
                />
              )}
            </div>
          </Card>

          {/* Color Palettes */}
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">🌈 Color Palettes</h3>
            <Tabs defaultValue="calm" className="w-full">
              <TabsList className="grid grid-cols-2 w-full mb-3 text-xs">
                <TabsTrigger value="calm">Calm</TabsTrigger>
                <TabsTrigger value="energetic">Energy</TabsTrigger>
              </TabsList>
              <TabsContent value="calm" className="space-y-3">
                {Object.entries(colorPalettes).filter(([key]) => ['calm', 'nature', 'cool'].includes(key)).map(([name, colors]) => (
                  <div key={name} className="space-y-1">
                    <p className="text-xs font-medium text-gray-700 capitalize">{name}</p>
                    <div className="grid grid-cols-7 gap-1">
                      {colors.map((color, index) => (
                        <button
                          key={index}
                          className="aspect-square rounded border-2 border-white shadow-sm hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => setCurrentColor(color)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </TabsContent>
              <TabsContent value="energetic" className="space-y-3">
                {Object.entries(colorPalettes).filter(([key]) => ['energetic', 'warm', 'sunset', 'rainbow'].includes(key)).map(([name, colors]) => (
                  <div key={name} className="space-y-1">
                    <p className="text-xs font-medium text-gray-700 capitalize">{name}</p>
                    <div className="grid grid-cols-7 gap-1">
                      {colors.map((color, index) => (
                        <button
                          key={index}
                          className="aspect-square rounded border-2 border-white shadow-sm hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => setCurrentColor(color)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </Card>

          {/* Quick Actions */}
          <Card className="glass-card p-4">
            <h3 className="font-semibold text-gray-900 mb-3">⚡ Quick Actions</h3>
            <div className="space-y-2">
              <Button 
                onClick={() => setCurrentColor('#000000')} 
                className="w-full text-left justify-start btn-secondary"
                variant="outline"
              >
                🖤 Black
              </Button>
              <Button 
                onClick={() => setCurrentColor('#FFFFFF')} 
                className="w-full text-left justify-start btn-secondary"
                variant="outline"
              >
                🤍 White
              </Button>
              <Button 
                onClick={() => {
                  const randomColor = `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`;
                  setCurrentColor(randomColor);
                }} 
                className="w-full text-left justify-start btn-secondary"
                variant="outline"
              >
                🎲 Random
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Tips */}
      <Card className="glass-card p-6 bg-gradient-to-r from-blue-50 to-cyan-50">
        <h4 className="font-semibold text-blue-800 mb-3">💡 Digital Art Tips for Youth</h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
          <ul className="space-y-2">
            <li>• Don't worry about making it "perfect" - express yourself!</li>
            <li>• Try different brush sizes for varied effects</li>
            <li>• Use the eraser creatively, not just for mistakes</li>
            <li>• Experiment with the spray tool for texture</li>
          </ul>
          <ul className="space-y-2">
            <li>• Mix colors by drawing over existing colors</li>
            <li>• Use templates as starting points, not limitations</li>
            <li>• Take breaks and come back with fresh eyes</li>
            <li>• Save your work frequently as you progress</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default DigitalCanvas;
